import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface DisabledTooltipButtonProps {
  label: string;
  tooltip: string;
  testId?: string;
  variant?: 'default' | 'outline';
  className?: string;
  wrapperClassName?: string;
  children: ReactNode;
}

// A disabled Button does not fire pointer events, so the tooltip trigger wraps
// it in a focusable span; wrapperClassName lets a full-width button stretch.
export function DisabledTooltipButton({
  label,
  tooltip,
  testId,
  variant = 'default',
  className,
  wrapperClassName,
  children,
}: Readonly<DisabledTooltipButtonProps>) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className={wrapperClassName}>
            <Button
              type="button"
              variant={variant}
              size="sm"
              disabled
              aria-label={label}
              data-testid={testId}
              className={className}
            >
              {children}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface DisabledIconButtonProps {
  icon: ReactNode;
  label: string;
  tooltip: string;
}

export function DisabledIconButton({ icon, label, tooltip }: Readonly<DisabledIconButtonProps>) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>
            <Button type="button" variant="ghost" size="icon" disabled aria-label={label}>
              {icon}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
